<template>
  <card-container
    :id="pageId"
    :disabled="isSubmitDisabled"
    :no-action="!isShowContentSection"
    @reset="resetForm"
    @submit="submitForm"
  >
    <template #title>
      Customer Information
    </template>

    <template #card-notes>
      <div class="card-notes">
        <p class="notification-message">
          If you see the registration data of previous customer, please click the Reset button
        </p>
      </div>
    </template>

    <template #content>
      <h6 class="sub-section-title">
        <div v-if="isGourmetRegistration">
          {{ $t('registration.whoWillBeUsing') }}
        </div>
        <div v-else>
          {{ $t('registration.whoWillBeRiding') }}
        </div>
      </h6>

      <div
        v-if="!isPartnerDashboard"
        class="row"
      >
        <label class="col-sm-3 control-label mt-1"> Who is the caller registering for today?* </label>
        <div class="col-sm-8 d-flex">
          <DropdownList
            v-model="softRegSenior"
            :options="softRegSeniorOptions"
            placeholder="Select One"
            required
          />
        </div>
      </div>

      <template v-if="isShowContentSection">
        <div
          v-if="isGourmetRegistration"
          class="operator-script-section"
        >
          <h6 class="script-title">
            Customer Needs
          </h6>
          <div class="script-content">
            <p class="script-text">
              "GoGo gives you access to lots of local restaurants in your area including
              <strong>{{ formattedGourmetStoreNames }}</strong>
              and more, as well as grocery delivery and rides. All of that is yours with just a phone call, and there is always a real person here to help."
            </p>
          </div>

          <div class="questionnaire-section">
            <p class="question-label">
              Do you expect this to be a one-time need, or do you expect to call us again? <span class="required">*</span>
            </p>
            <div class="toggle-button-group">
              <button
                v-for="option in gourmetUsageOptions"
                :key="option.value"
                type="button"
                class="toggle-button"
                :class="{ active: gourmetUsage === option.value }"
                @click="gourmetUsage = option.value"
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <div
            v-if="gourmetUsage === 'multiple'"
            class="questionnaire-section"
          >
            <p class="question-label">
              About how many times per month do you expect to order? <span class="required">*</span>
            </p>
            <div class="toggle-button-group">
              <button
                v-for="option in gourmetOrderFrequencyOptions"
                :key="option.value"
                type="button"
                class="toggle-button"
                :class="{ active: gourmetUsageFrequency === option.value }"
                @click="gourmetUsageFrequency = option.value"
              >
                {{ option.label }}
              </button>
            </div>
          </div>
        </div>

        <div class="row">
          <label class="col-sm-3 control-label mt-10"> Name* </label>
          <div class="col-sm-3">
            <sanitized-input
              id="firstName"
              v-model="callerInfo.firstName"
              placeholder="John"
              required
            />
            <span class="help-block">First Name*</span>
          </div>

          <div class="col-sm-3">
            <sanitized-input
              id="lastName"
              v-model="callerInfo.lastName"
              placeholder="Doe"
              required
            />
            <span class="help-block">Last Name*</span>
          </div>
        </div>

        <div class="row align-items-center">
          <label class="col-sm-3 control-label"> Birthday </label>
          <div class="col-sm-2 col-4 mobile-pr-0">
            <DropdownList
              v-model="callerInfo.dobMonth"
              clearable
              placeholder="--Month--"
            >
              <template #additional-options-prefix>
                <el-option
                  v-for="month in 12"
                  :key="month"
                  :label="month"
                  :value="month"
                >
                  {{ month }}
                </el-option>
              </template>
            </DropdownList>
          </div>

          <div class="col-sm-2 col-4">
            <DropdownList
              v-model="callerInfo.dobDay"
              clearable
              filterable
              placeholder="--Day--"
            >
              <template #additional-options-prefix>
                <el-option
                  v-for="day in 31"
                  :key="day"
                  :label="day"
                  :value="day"
                >
                  {{ day }}
                </el-option>
              </template>
            </DropdownList>
          </div>

          <div class="col-sm-2 col-4 mobile-pl-0">
            <DropdownList
              v-model="callerInfo.dobYear"
              clearable
              filterable
              placeholder="--Year--"
            >
              <template #additional-options-prefix>
                <el-option
                  v-for="year in getYearList()"
                  :key="year"
                  :label="year"
                  :value="year"
                >
                  {{ year }}
                </el-option>
              </template>
            </DropdownList>
          </div>
        </div>

        <h6 class="sub-section-title">
          Contact Information
        </h6>

        <fieldset>
          <div
            v-if="isMaskedPhoneNumberCall"
            class="text-center mb-2 text-red font-weight-bold font-italic"
          >
            Do not copy the phone number from Slack or anywhere else. Please ask the customer for their phone number.
          </div>
          <div class="row align-items-center">
            <label class="col-sm-3 control-label"> Mobile Phone* </label>
            <phone-number-input
              id="customer-phone"
              v-model="callerInfo.phoneNumber"
              :page-id="pageId"
              class="col-sm-5 mobile-pb-10"
              @onblur="validatePhoneNumber"
            />
          </div>

          <div class="row align-items-center">
            <label class="col-sm-3 control-label"> Home Number </label>
            <phone-number-input
              id="home-phone"
              v-model="callerInfo.homeNumber"
              :page-id="pageId"
              :required="false"
              class="col-sm-5"
            />
          </div>
          <div class="row">
            <label class="col-sm-3 control-label pt-10"> Email </label>
            <div class="col-sm-5">
              <email-input
                v-model="callerInfo.email"
                @update-validation="updateEmailValidation"
              />
            </div>
          </div>
          <div class="row align-items-center">
            <div class="col-sm-5 offset-sm-3">
              <el-checkbox
                id="isAskedUserForEmail"
                v-model="callerInfo.isAskedUserForEmail"
              >
                I have asked the customer for an e-mail and they do not have one
              </el-checkbox>
            </div>
          </div>

          <template v-if="isPartnerDashboard">
            <div
              v-if="isPartnerFieldVisible('externalPartnerCallerId')"
              class="row"
            >
              <label class="col-sm-3 control-label pt-10">
                {{ getPartnerFieldLabel({ field: 'externalPartnerCallerId', defaultValue: 'External Caller ID' }) }}
              </label>
              <div class="col-sm-5">
                <sanitized-input
                  id="externalPartnerCallerId"
                  v-model="callerInfo.externalPartnerCallerId"
                  :placeholder="getPartnerFieldPlaceholder('externalPartnerCallerId') || 'External Caller ID'"
                  :required="isPartnerFieldRequired('externalPartnerCallerId')"
                  :validate="validateExternalPartnerCallerId"
                />
              </div>
            </div>

            <div
              v-if="isPartnerFieldVisible('preAuthorizationNumber')"
              class="row"
            >
              <label class="col-sm-3 control-label pt-10">
                {{ getPartnerFieldLabel({ field: 'preAuthorizationNumber', defaultValue: 'Pre-authorization #' }) }}
              </label>
              <div class="col-sm-5">
                <sanitized-input
                  id="preAuthorizationNumber"
                  v-model="callerInfo.preAuthorizationNumber"
                  :placeholder="getPartnerFieldPlaceholder('preAuthorizationNumber')"
                  :required="isPartnerFieldRequired('preAuthorizationNumber')"
                  :validate="validatePreAuthorizationNumber"
                />
              </div>
            </div>

            <div
              v-if="partnerInfo?.serviceCode"
              class="row"
            >
              <label class="col-sm-3 control-label pt-10">
                {{ getPartnerFieldLabel({ field: 'partnerServiceCode', defaultValue: 'Service Code', required: false }) }}
              </label>
              <div class="col-sm-5">
                <div
                  id="partnerServiceCode"
                  class="content-view"
                >
                  {{ partnerInfo.serviceCode }}
                </div>
              </div>
            </div>

            <div
              v-if="isPartnerFieldVisible('partnerBeginningDate')"
              class="row"
            >
              <label class="col-sm-3 control-label pt-10">
                {{ getPartnerFieldLabel({ field: 'partnerBeginningDate', defaultValue: 'Begin Date' }) }}
              </label>
              <div class="col-sm-5">
                <date-picker
                  v-model="callerInfo.partnerBeginningDate"
                  :required="isPartnerFieldRequired('partnerBeginningDate')"
                />
              </div>
            </div>

            <div
              v-if="isPartnerFieldVisible('partnerSuspensionDate')"
              class="row"
            >
              <label class="col-sm-3 control-label pt-10">
                {{ getPartnerFieldLabel({ field: 'partnerSuspensionDate', defaultValue: 'Suspension Date' }) }}
              </label>
              <div class="col-sm-5">
                <date-picker
                  v-model="callerInfo.partnerSuspensionDate"
                  :required="isPartnerFieldRequired('partnerSuspensionDate')"
                />
              </div>
            </div>

            <div
              v-if="isPartnerFieldVisible('partnerMonthlyBudgetAmount')"
              class="row"
            >
              <label class="col-sm-3 control-label pt-10">
                {{ getPartnerFieldLabel({ field: 'partnerMonthlyBudgetAmount', defaultValue: 'Monthly Budget Amount' }) }}
              </label>
              <div class="col-sm-5">
                <el-input
                  v-model="callerInfo.partnerMonthlyBudgetAmount"
                  type="number"
                  step="0.01"
                  :min="0"
                  :required="isPartnerFieldRequired('partnerMonthlyBudgetAmount')"
                >
                  <template #prepend>
                    $
                  </template>
                </el-input>
              </div>
            </div>

            <div
              v-if="isPartnerFieldVisible('partnerTotalBudgetAmount')"
              class="row"
            >
              <label class="col-sm-3 control-label pt-10">
                {{ getPartnerFieldLabel({ field: 'partnerTotalBudgetAmount', defaultValue: 'Total Budget Amount' }) }}
              </label>
              <div class="col-sm-5">
                <el-input
                  v-model="callerInfo.partnerTotalBudgetAmount"
                  type="number"
                  step="0.01"
                  :min="0"
                  :required="isPartnerFieldRequired('partnerTotalBudgetAmount')"
                >
                  <template #prepend>
                    $
                  </template>
                </el-input>
              </div>
            </div>

            <template v-if="isAllowanceTierVisible">
              <div
                v-if="!isOnlyCustomRideAllowanceTier"
                class="row"
              >
                <label class="col-sm-3 control-label pt-10">
                  {{ getPartnerFieldLabel({ field: 'defaultRideAllowanceTier', defaultValue: 'Allowance Tier', required: isAllowanceTierRequired }) }}
                </label>
                <div class="col-sm-5">
                  <DropdownList
                    v-model="callerInfo.defaultRideAllowanceTier"
                    :options="partnerRideAllowanceTiers"
                    :required="isAllowanceTierRequired"
                  />
                </div>
              </div>

              <div
                v-if="isCustomRideAllowanceTier"
                class="row"
              >
                <label class="col-sm-3 control-label pt-10">
                  {{ getPartnerFieldLabel({ field: 'defaultRideAllowanceAmount', defaultValue: 'Allowance Tier Amount', required: true }) }}
                </label>
                <div class="col-sm-5">
                  <number-input
                    v-model="callerInfo.defaultRideAllowanceAmount"
                    type="number"
                    step="1"
                    :min="0"
                    :required="isCustomRideAllowanceTier"
                    class="form-control available-rides-input"
                    :placeholder="getPartnerFieldPlaceholder('defaultRideAllowanceAmount') || 'Enter Allowance Tier Amount'"
                  />
                </div>
              </div>

              <div class="row">
                <label class="col-sm-3 control-label pt-10"> Notes </label>
                <div class="col-sm-5">
                  <sanitized-textarea
                    v-model="callerInfo.partnerCallerNotes"
                    maxlength="255"
                  />
                </div>
              </div>
            </template>

            <PartnerMemberBudgets
              v-if="isPartnerFieldVisible('budgetRides') || isPartnerFieldVisible('budgetCredits')"
              :groups="callerPartnerBudgets.visibleGroups"
              :is-loading="callerPartnerBudgets.isLoading"
              :load-error="callerPartnerBudgets.loadError"
              @update-balance="callerPartnerBudgets.updateBalance"
              @remove-balance="callerPartnerBudgets.removeBalance"
            />

          </template>
        </fieldset>

        <div v-if="isPartnerEmergencyContactVisible">
          <h6 class="sub-section-title">
            {{ getPartnerFieldLabel({ field: 'partnerEmergencyContact', defaultValue: 'Service Coordinator Contact', required: false }) }}
          </h6>

          <fieldset>
            <div class="row">
              <label class="col-sm-3 control-label mt-10"> {{ isPartnerEmergencyContactRequired ? 'Name*' : 'Name' }} </label>

              <div class="col-sm-3">
                <sanitized-input
                  id="firstName"
                  v-model="callerInfo.partnerEmergencyContact.firstName"
                  :placeholder="getPartnerFieldPlaceholder('partnerEmergencyContactFirstName') || 'John'"
                  :required="isPartnerEmergencyContactRequired"
                />
                <span class="help-block">{{
                  getPartnerFieldLabel({
                    field: 'partnerEmergencyContactFirstName',
                    defaultValue: 'First Name',
                    required: isPartnerEmergencyContactRequired,
                  })
                }}</span>
              </div>

              <div class="col-sm-3">
                <sanitized-input
                  id="lastName"
                  v-model="callerInfo.partnerEmergencyContact.lastName"
                  :placeholder="getPartnerFieldPlaceholder('partnerEmergencyContactLastName') || 'Doe'"
                  :required="isPartnerEmergencyContactRequired"
                />
                <span class="help-block">{{
                  getPartnerFieldLabel({
                    field: 'partnerEmergencyContactLastName',
                    defaultValue: 'Last Name',
                    required: isPartnerEmergencyContactRequired,
                  })
                }}</span>
              </div>
            </div>
            <div class="row">
              <label class="col-sm-3 control-label pt-10">{{
                getPartnerFieldLabel({
                  field: 'partnerEmergencyContactEmail',
                  defaultValue: 'Email',
                  required: isPartnerEmergencyContactRequired,
                })
              }}</label>
              <div class="col-sm-5">
                <email-input
                  v-model="callerInfo.partnerEmergencyContact.email"
                  :required="isPartnerEmergencyContactRequired"
                  skip-email-validation
                  @update-validation="updateEmailValidation"
                />
              </div>
            </div>

            <div class="row align-items-center">
              <label class="col-sm-3 control-label">{{
                getPartnerFieldLabel({
                  field: 'partnerEmergencyContactPhoneNumber',
                  defaultValue: 'Phone Number',
                  required: isPartnerEmergencyContactRequired,
                })
              }}</label>
              <phone-number-input
                id="partner-emergency-phone-number"
                v-model="callerInfo.partnerEmergencyContact.phoneNumber"
                :page-id="pageId"
                class="col-sm-5 mobile-pb-10"
                :required="isPartnerEmergencyContactRequired"
              />
            </div>
          </fieldset>
        </div>

        <div v-if="isPartnerProgramManagerVisibleAndRequired">
          <h6 class="sub-section-title">
            {{
              getPartnerFieldLabel({ field: 'partnerProgramManager', defaultValue: 'Program Manager Contact', required: false })
            }}
          </h6>

          <fieldset>
            <div class="row">
              <label class="col-sm-3 control-label mt-10"> Name* </label>

              <div class="col-sm-3">
                <sanitized-input
                  id="programManagerFirstName"
                  v-model="callerInfo.partnerProgramManager.firstName"
                  :placeholder="getPartnerFieldPlaceholder('partnerProgramManagerFirstName') || 'John'"
                  required
                />
                <span class="help-block">{{
                  getPartnerFieldLabel({
                    field: 'partnerProgramManagerFirstName',
                    defaultValue: 'First Name',
                    required: true,
                  })
                }}</span>
              </div>

              <div class="col-sm-3">
                <sanitized-input
                  id="programManagerLastName"
                  v-model="callerInfo.partnerProgramManager.lastName"
                  :placeholder="getPartnerFieldPlaceholder('partnerProgramManagerLastName') || 'Doe'"
                  required
                />
                <span class="help-block">{{
                  getPartnerFieldLabel({
                    field: 'partnerProgramManagerLastName',
                    defaultValue: 'Last Name',
                    required: true,
                  })
                }}</span>
              </div>
            </div>
            <div class="row">
              <label class="col-sm-3 control-label pt-10">{{
                getPartnerFieldLabel({
                  field: 'partnerProgramManagerEmail',
                  defaultValue: 'Email',
                  required: true,
                })
              }}</label>
              <div class="col-sm-5">
                <email-input
                  v-model="callerInfo.partnerProgramManager.email"
                  required
                  skip-email-validation
                  @update-validation="updateEmailValidation"
                />
              </div>
            </div>

            <div class="row align-items-center">
              <label class="col-sm-3 control-label">{{
                getPartnerFieldLabel({
                  field: 'partnerProgramManagerPhoneNumber',
                  defaultValue: 'Phone Number',
                  required: false,
                })
              }}</label>
              <phone-number-input
                id="partner-program-manager-phone-number"
                v-model="callerInfo.partnerProgramManager.phoneNumber"
                :page-id="pageId"
                class="col-sm-5 mobile-pb-10"
              />
            </div>
          </fieldset>
        </div>

        <h6 class="sub-section-title">
          Home Address
        </h6>

        <expand-address-section
          v-model="callerAddress"
          :map-type-id="mapTypeId"
          :parent-id="pageId"
          :show-geocode="false"
          :show-gourmet-note="isGourmetRegistration"
          :zoom="18"
          class="mobile-mt-0"
          is-editing
          open-map-default
          side-by-side-map
        >
          <template #noteToDriver>
            <el-tooltip
              v-if="!isGroceryRegistration"
              class="item"
              effect="dark"
              placement="top"
              popper-class="custom-tooltip note-info"
            >
              <template #content>
                <span> The note will be sent to the driver on all home pick-ups. </span>
              </template>

              <label class="col-sm-3 control-label pt-10"> Home Note To Driver <i class="fa fa-info-circle" /> </label>
            </el-tooltip>
          </template>
        </expand-address-section>
        <div class="form-submit-button-group mb-20">
          <p-button
            round
            type="danger"
            @click="resetAddress"
          >
            Reset Address
          </p-button>
        </div>

        <template v-if="isPartnerDashboard">
          <h6 class="sub-section-title">
            Mailing Address
          </h6>

          <div class="row align-items-center">
            <div class="col-sm-8 offset-3">
              <el-checkbox v-model="isSameAsHomeAddress">
                Same as Home Address
              </el-checkbox>
            </div>
          </div>

          <expand-address-section
            v-model="mailingAddress"
            :is-editing="!isSameAsHomeAddress"
            :map-type-id="mapTypeId"
            :parent-id="pageId"
            :show-geocode="false"
            :show-home-note="false"
            :show-map="false"
          />
        </template>

        <template v-if="isPartnerDashboard && isApprovedAddressPartner">
          <h6 class="sub-section-title">
            Approved Addresses
          </h6>
          <div
            v-for="(_, idx) in approvedRegistrationAddresses"
            :key="`approved-reg-${idx}`"
            class="mb-15"
          >
            <expand-address-section
              v-model="approvedRegistrationAddresses[idx]"
              :map-type-id="mapTypeId"
              :parent-id="`${pageId}-approved-${idx}`"
              :show-geocode="true"
              :show-home-note="false"
              :zoom="18"
              is-editing
              open-map-default
              side-by-side-map
            />
            <div class="form-submit-button-group">
              <p-button
                round
                type="danger"
                @click="removeApprovedRegistrationRow(idx)"
              >
                Remove address
              </p-button>
            </div>
          </div>
          <p-button
            class="mb-20"
            round
            type="default"
            @click="addApprovedRegistrationRow"
          >
            Add New Approved Address
          </p-button>
        </template>

        <h6 class="sub-section-title">
          Language Information
        </h6>

        <fieldset>
          <div class="row align-items-center">
            <label class="col-sm-3 control-label"> Primary Language </label>
            <div class="col-sm-3 mobile-pb-10">
              <DropdownList
                v-model="callerInfo.language1"
                :options="languageList"
              />
            </div>

            <label class="col-sm-2 control-label"> Secondary Language </label>
            <div class="col-sm-3">
              <DropdownList
                v-model="callerInfo.language2"
                :options="languageList"
              />
            </div>
          </div>

          <div class="row align-items-center">
            <label class="col-sm-3 control-label">
              Language Preference
              <br class="is-desktop">For Reading
            </label>
            <div class="col-sm-3 mobile-pb-10">
              <DropdownList
                v-model="callerInfo.langPrefForUI"
                :options="languagePreferenceList"
                :value-key="isUpdatingStep ? 'id' : 'key'"
              />
            </div>

            <label class="col-sm-2 control-label"> Language Preference For Operator </label>
            <div class="col-sm-3">
              <DropdownList
                v-model="callerInfo.langPrefForOperator"
                :options="languagePreferenceList"
                :value-key="isUpdatingStep ? 'id' : 'key'"
              />
            </div>
          </div>

          <div class="row align-items-center">
            <label class="col-sm-3 control-label">
              Language Preference
              <br class="is-desktop">For Driver
            </label>
            <div class="col-sm-3">
              <DropdownList
                v-model="callerInfo.langPrefForDriver"
                :options="languagePreferenceList"
                :value-key="isUpdatingStep ? 'id' : 'key'"
              />
            </div>
          </div>
        </fieldset>

        <template v-if="isGourmetRegistration">
          <h6 class="sub-section-title">
            Food Allergy / Dietary Restriction
          </h6>

          <div class="row mobile-mb-0">
            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isNoRedMeat"
                v-model="callerInfo.isNoRedMeat"
              >
                No Red Meat
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isNutAllergy"
                v-model="callerInfo.isNutAllergy"
              >
                Nut Allergy
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isDairyAllergy"
                v-model="callerInfo.isDairyAllergy"
              >
                Dairy Allergy
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isGlutenFree"
                v-model="callerInfo.isGlutenFree"
              >
                Gluten Free
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isVegan"
                v-model="callerInfo.isVegan"
              >
                Vegan
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isVegetarian"
                v-model="callerInfo.isVegetarian"
              >
                Vegetarian
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isLowSugarDiet"
                v-model="callerInfo.isLowSugarDiet"
              >
                Low Sugar Diet
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isLowFatsDiet"
                v-model="callerInfo.isLowFatsDiet"
              >
                Low Fats Diet
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isLowFiberDiet"
                v-model="callerInfo.isLowFiberDiet"
              >
                Low Fiber Diet
              </el-checkbox>
            </div>
          </div>
        </template>

        <h6 class="sub-section-title">
          Additional Information
        </h6>

        <fieldset>
          <div class="row mobile-mb-0">
            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="noAdditionalInfo"
                v-model="callerInfo.noAdditionalInfo"
              >
                None
              </el-checkbox>
            </div>
          </div>

          <div class="row mobile-mb-0">
            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isVisuallyImpaired"
                v-model="callerInfo.isVisuallyImpaired"
                :disabled="callerInfo.noAdditionalInfo"
              >
                Visually Impaired
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isCane"
                v-model="callerInfo.isCane"
                :disabled="callerInfo.noAdditionalInfo"
              >
                Uses a Cane / Leg braces
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isElbowAssist"
                v-model="callerInfo.isElbowAssist"
                :disabled="callerInfo.noAdditionalInfo"
              >
                <el-tooltip
                  class="item"
                  effect="dark"
                  placement="top"
                  popper-class="custom-tooltip note-info"
                >
                  <template #content>
                    Check this box to send a message to each assigned driver requesting assistance in and out of the vehicle.
                    A $5 fee per ride applies, delivered to the driver as a tip.
                  </template>
                  <span> Needs Elbow Assist <i class="fa fa-info-circle absolute-top-right" /> </span>
                </el-tooltip>
              </el-checkbox>
            </div>
            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isTraveling"
                v-model="callerInfo.isTraveling"
                :disabled="callerInfo.noAdditionalInfo"
              >
                Is Traveling
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isWalker"
                v-model="callerInfo.isWalker"
                :disabled="callerInfo.noAdditionalInfo"
              >
                Uses a Walker / Crutches
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isWheelchair"
                v-model="callerInfo.isWheelchair"
                :disabled="callerInfo.noAdditionalInfo"
              >
                Manual Wheelchair
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isPowerWheelchairScooter"
                v-model="callerInfo.isPowerWheelchairScooter"
                :disabled="callerInfo.noAdditionalInfo"
              >
                Power Wheelchair / Scooter
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isHardOfHearing"
                v-model="callerInfo.isHardOfHearing"
                :disabled="callerInfo.noAdditionalInfo"
              >
                Hard of Hearing
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isServiceDog"
                v-model="callerInfo.isServiceDog"
                :disabled="callerInfo.noAdditionalInfo"
              >
                Service Dog<span v-show="callerInfo.isServiceDog">&nbsp;&nbsp;🐶</span>
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isOxygenTank"
                v-model="callerInfo.isOxygenTank"
                :disabled="callerInfo.noAdditionalInfo"
              >
                Portable Oxygen Tank / Respirator
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isPoorMemory"
                v-model="callerInfo.isPoorMemory"
                :disabled="callerInfo.noAdditionalInfo"
              >
                Poor Memory
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isNoBigCars"
                v-model="callerInfo.isNoBigCars"
                :disabled="callerInfo.noAdditionalInfo"
              >
                Cannot get into big cars
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isNoSmallCars"
                v-model="callerInfo.isNoSmallCars"
                :disabled="callerInfo.noAdditionalInfo"
              >
                Cannot get into small cars
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isVerballyImpaired"
                v-model="callerInfo.isVerballyImpaired"
                :disabled="callerInfo.noAdditionalInfo"
              >
                Verbally Impaired
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isSlowWalker"
                v-model="callerInfo.isSlowWalker"
                :disabled="callerInfo.noAdditionalInfo"
              >
                Slow Walker
              </el-checkbox>
            </div>

            <div class="col-sm-3 col-6 checkbox-container">
              <el-checkbox
                id="isAda"
                v-model="callerInfo.isAda"
                :disabled="callerInfo.noAdditionalInfo"
              >
                Has ADA Eligible Disability
              </el-checkbox>
            </div>
          </div>

          <template v-if="!isPartnerDashboard">
            <div class="row">
              <label class="col-sm-3 control-label pt-5 mobile-pt-0"> Operator Note </label>
              <div class="col-sm-8">
                <sanitized-textarea
                  id="operatorNote"
                  v-model="callerInfo.operatorNote"
                  autosize
                  placeholder="What else do we need to know to make your ride a success?"
                />
              </div>
            </div>

            <div class="row align-items-center">
              <label class="col-sm-3 control-label"> Is Soft Registration </label>
              <div class="col-sm-3">
                <el-checkbox
                  id="isSoftRegistration"
                  v-model="callerInfo.isSoftRegistration"
                >
                  &nbsp;
                </el-checkbox>
              </div>
            </div>
            <div
              v-if="isGroceryRegistration"
              class="row"
            >
              <label class="col-sm-3 control-label">Special Instructions for shopper*</label>
              <div class="col-sm-8">
                <sanitized-textarea
                  id="specialInstructions"
                  v-model="callerInfo.specialInstructions"
                  autosize
                  placeholder="Please note the special instructions."
                  required
                />
              </div>
            </div>

            <div
              v-if="isGroceryRegistration"
              class="row align-items-center"
            >
              <label class="col-sm-3 control-label">Disability Type*</label>
              <div class="col-sm-8">
                <DropdownList
                  v-model="callerInfo.disabilityType"
                  :options="disabilityTypeList"
                  required
                />
              </div>
            </div>
          </template>
        </fieldset>
      </template>
    </template>

    <template #submit-button>
      {{ callerInfo.id ? 'Update' : isGourmetRegistration ? $t('registration.createGourmetUser') : $t('registration.createRideUser') }}
    </template>
  </card-container>
</template>

<script lang="ts" setup>
import { ref, Ref, computed, watch, onMounted, onBeforeUnmount, reactive } from 'vue';
import { ElLoading } from 'element-plus';

import util from '@/Utils/util';
import { sendPost } from '@/Utils/api';
import { useDashboard } from '@/use/app';
import { useAddress } from '@/use/address';
import { useSwalDialog } from '@/use/swalDialog';
import { useCustomer } from '@/use/customer';
import { useGourmet } from '@/use/gourmet';
import { usePartner } from '@/use/partner';
import { useGrocery } from '@/use/grocery';
import { useEventListener } from '@/use/eventListener';
import { CustomLocationType } from '@/models/address';
import { ListType } from '@/models/app';
import { GVRA_PARTNERS } from '@/models/partner';
import { languageList } from '@/constants/app';
import { languagePreferenceList, minAge, maxAge } from '@/constants/caller';
import { disabilityTypeList } from '@/constants/grocery';
import { emptyAddress } from '@/constants/ride';
import { currentCallFromPhoneNumber, currentCallLog, isFacebookAdCall, isMiamiDadePartnershipCall } from '@/services/callLogs/callLogsViewModel';
import { gourmetUsageFrequency, gourmetUsage, gourmetUsageOptions, gourmetOrderFrequencyOptions, trialInfo, gourmetUsageQqqGroup } from '@/services/abTests/abTestsViewModel';
import { abTestService } from '@/services/abTests';
import { partnerCallerService } from '@/services/partnerCaller';
import { callerService } from '@/services/caller';
import { userInteractionService } from '@/services/userInteraction';

import DropdownList from '@/components/UIComponents/DropdownList.vue';
import PartnerMemberBudgets from '@/components/Main/Operator/Registration/PartnerMemberBudgets.vue';
import { useCallerPartnerBudgets } from '@/components/Main/Operator/Registration/useCallerPartnerBudgets';
import {
  buildEmptyBudgetChanges,
  type PartnerMemberBudgetChanges,
  type PartnerMemberBudgets as PartnerMemberBudgetsData,
} from '@/components/Main/Operator/Registration/partnerMemberBudget';
import { updateSubsidyAllowanceBalances } from '@/services/partner-subsidy/updateSubsidyAllowanceBalances';
import { CallerAdditionalInfoProps, type CallerAddressInput } from '@/models/caller';
import { planCurrency, planPricing } from '@/services/pricing';

const pageId = 'create-update-customer';

const props = defineProps({
  partnerCode: {
    type: String,
    default: '',
  },

  isGourmetRegistration: {
    type: Boolean,
    default: false,
  },

  isGroceryRegistration: {
    type: Boolean,
    default: false,
  },

  mapTypeId: {
    type: String,
    default: 'satellite',
  },
});

const emit = defineEmits(['goto-next-step', 'valid']);

const gourmet7DaysTrialAnyAbTest = 'gourmet_usage_qqq_7_day_trial_any';
const gourmet7DaysTrialAnnualAbTest = 'gourmet_usage_qqq_7_day_trial_annual';
const gourmetUsageQqqAbTestName = 'gourmet_usage_qqq';
const { isPartnerDashboard } = useDashboard();
const { checkForAddressDeliverability, trimAddress, registrationStartAddress, registrationEndAddress } = useAddress();
const { showSuccessPopupDialog, showErrorPopupDialog, showWarningPopupDialog, showConfirmPopupDialog } = useSwalDialog();
const { gourmetRegistrationStoreNames } = useGourmet();
const {
  newCustomerInfo,
  setNewCustomerInfo,
} = useCustomer();
const { groceryAddress } = useGrocery();
const {
  partnerInfo,
  partnerCode: partnerCodeRef,
  partnerId,
  isPartnerFieldVisible,
  isPartnerFieldRequired,
  getPartnerFieldLabel,
  getPartnerFieldPlaceholder,
  isExternalPartnerCallerIdValid,
  validateExternalPartnerCallerId,
  isPreAuthorizationNumberValid,
  validatePreAuthorizationNumber,
  partnerRideAllowanceTiers,
  isOnlyCustomRideAllowanceTier,
  isAllowanceTierVisible,
  isAllowanceTierRequired,
  isPartnerProgramManagerVisibleAndRequired,
  isApprovedAddressPartner,
} = usePartner();

const callerInfo: Ref<any> = ref({
  phoneNumber: '',
  homeNumber: '',
  partnerEmergencyContact: {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  },
  partnerProgramManager: {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  },
});

const budgetCallerId = computed(() => {
  const id = callerInfo.value.id || newCustomerInfo.value?.id;
  return id ? String(id) : null;
});

const budgetPartnerCodeName = computed(() => props.partnerCode || partnerCodeRef.value || '');
const partnerBudgets = ref<PartnerMemberBudgetsData>({ groups: [] });
const partnerBudgetChanges = ref<PartnerMemberBudgetChanges>(buildEmptyBudgetChanges());

const callerPartnerBudgets = reactive(useCallerPartnerBudgets({
  budgets: partnerBudgets,
  changes: partnerBudgetChanges,
  callerId: budgetCallerId,
  partnerCodeName: budgetPartnerCodeName,
}));

const savePartnerBudgets = async () => {
  if (!callerPartnerBudgets.hasBalanceChanges) {
    return;
  }
  try {
    await updateSubsidyAllowanceBalances({
      partnerCodeName: budgetPartnerCodeName.value,
      callerId: String(callerInfo.value.id),
      ...callerPartnerBudgets.balanceChanges,
    });
  } catch (error) {
    showErrorPopupDialog({
      text: util.getGraphqlError(error, false),
    });
    return;
  }
  await callerPartnerBudgets.loadBudgets();
};

const callerAddress: Ref<CustomLocationType> = ref({ ...emptyAddress });
const mailingAddress: Ref<CustomLocationType> = ref({ ...emptyAddress });

const approvedRegistrationAddresses = ref<CallerAddressInput[]>([]);

const addApprovedRegistrationRow = () => {
  approvedRegistrationAddresses.value.push({ ...emptyAddress });
};

const removeApprovedRegistrationRow = (idx: number) => {
  approvedRegistrationAddresses.value.splice(idx, 1);
};

const isHomeAddressChanged = ref<boolean>(false);
const lastPhoneNumber = ref<string>('');
const isSameAsHomeAddress = ref<boolean>(false);
const isEmailValid = ref<boolean>(true);
const softRegSenior = ref<string>('');
const softRegSeniorOptions: ListType[] = [
  { id: 1, key: 'self', label: 'Self' },
  { id: 2, key: 'lovedOne', label: 'Loved One' },
];

const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(callerInfo.value.phoneNumber));
const isUpdatingStep = computed(() => !!newCustomerInfo.value?.id);

const formattedGourmetStoreNames = computed(() => {
  if (!gourmetRegistrationStoreNames.value.length) {
    return 'local restaurants';
  }
  return gourmetRegistrationStoreNames.value.join(', ');
});

const isSoftRegSenior = computed(() => softRegSenior.value === 'self');

const isGourmetQuestionnaireIncomplete = computed(() => {
  if (!props.isGourmetRegistration) return false;
  if (!gourmetUsage.value) return true;
  if (gourmetUsage.value === 'multiple' && !gourmetUsageFrequency.value) return true;
  return false;
});

const isSubmitDisabled = computed(() => {
  return !isPhoneNumberValid.value
    || !isEmailValid.value
    || !isPartnerEmergencyContactOk.value
    || !isPartnerProgramManagerOk.value
    || !isExternalPartnerCallerIdValid.value
    || !isPreAuthorizationNumberValid.value
    || (!props.isGroceryRegistration && isGourmetQuestionnaireIncomplete.value);
});

const isPartnerEmergencyPhoneNumberValid = computed(() => util.isPhoneNumberValid(callerInfo.value.partnerEmergencyContact?.phoneNumber));

const isPartnerEmergencyContactVisible = computed(() => isPartnerFieldVisible('partnerEmergencyContact'));
const isPartnerEmergencyContactRequired = computed(() => isPartnerFieldRequired('partnerEmergencyContact'));

const isPartnerEmergencyContactOk = computed(() => {
  // if it's required, it must be valid
  // if it isn't required, it can be empty
  return !isPartnerEmergencyContactRequired.value || isPartnerEmergencyPhoneNumberValid.value;
});

const isPartnerProgramManagerPhoneNumberValid = computed(() => {
  const phoneNumber = callerInfo.value.partnerProgramManager?.phoneNumber;
  return !phoneNumber || util.isPhoneNumberValid(phoneNumber);
});

const isPartnerProgramManagerOk = computed(() => {
  if (!isPartnerProgramManagerVisibleAndRequired.value) {
    return true;
  }

  const programManager = callerInfo.value.partnerProgramManager;
  return !!(programManager?.firstName && programManager?.lastName && programManager?.email && isPartnerProgramManagerPhoneNumberValid.value);
});

const isCustomRideAllowanceTier = computed(
  () => isOnlyCustomRideAllowanceTier.value || callerInfo.value.defaultRideAllowanceTier === 'custom',
);

const isMaskedPhoneNumberCall = computed(() => isFacebookAdCall.value || isMiamiDadePartnershipCall.value);

const isShowContentSection = computed(() => softRegSenior.value || isUpdatingStep.value || isPartnerDashboard.value);

const getYearList = () => {
  const minYear = new Date().getFullYear() - maxAge;
  const maxYear = new Date().getFullYear() - minAge;

  return new Array(maxYear - minYear).fill(minYear).map((year: number, ind: number) => year + ind + 1);
};

const validatePhoneNumber = async () => {
  if (!util.isPhoneNumberValid(callerInfo.value.phoneNumber)) {
    return;
  }

  if (lastPhoneNumber.value === callerInfo.value.phoneNumber) {
    return;
  }

  const loading = ElLoading.service({
    lock: true,
    text: 'Checking Phone Number...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  await checkVoIP(callerInfo.value.phoneNumber);
  await checkDuplicateCustomer(callerInfo.value.phoneNumber);

  lastPhoneNumber.value = callerInfo.value.phoneNumber;
  loading.close();
};

const checkVoIP = async (phoneNumber: string) => {
  if (isPartnerDashboard.value) {
    return;
  }

  try {
    const isVoip = await callerService.checkIsVoip(phoneNumber);

    if (isVoip) {
      showWarningPopupDialog({
        text: 'The customer phone number is VoIP and so could be a fraud user. Please ask for another phone number with an official phone company',
      });
    }
  } catch {
    // Voip is checking in the background and we don't need to show error message.
  }
};

const checkDuplicateCustomer = async (phoneNumber: string) => {
  if (!isPartnerDashboard.value) {
    return;
  }

  try {
    const response = await callerService.fetchCustomerInfo({
      phoneNumber,
    });

    if (!response) {
      return;
    }

    let message = `We found that this phone number (${phoneNumber}) is already associated with an existing account. To avoid any service conflicts, please contact enterprise@gogograndparent to assist with switching this client over or resolving any account overlap.`;

    if (partnerCodeRef.value && response.partner?.codeName === partnerCodeRef.value) {
      const memberName = `${response.firstName} ${response.lastName}`;
      message = `This phone number (${phoneNumber}) is already assigned to an account within your partnership and is attached to member: ${memberName}`;
    }

    showErrorPopupDialog({
      text: message,
    });
  } catch {
    // Duplicate customer is checking in the background and we don't need to show error message.
  }
};

const updateEmailValidation = (value: boolean) => {
  isEmailValid.value = value;
};

const createCustomer = async (postData: any) => {
  try {
    const response = await sendPost('callers', postData);

    await showSuccessPopupDialog({
      text: 'Created customer successfully!',
    });

    callerInfo.value.id = response.data.id;
    callerInfo.value.referralCode = response.data.referralCode;

    // Post /callers and updateCaller mutation have difference value for preference languages
    // Post /callers has string value, updateCaller mutation has number, so after cx is created, we need to update them with interger to use them in updateCaller mutation
    callerInfo.value.langPrefForUI = response.data.langPrefForUI;
    callerInfo.value.langPrefForOperator = response.data.langPrefForOperator;
    callerInfo.value.langPrefForDriver = response.data.langPrefForDriver;
    callerInfo.value.streetAddress = response.data.streetAddress;
    callerInfo.value.streetAddress2 = response.data.streetAddress2;
    callerInfo.value.city = response.data.city;
    callerInfo.value.state = response.data.state;
    callerInfo.value.zip = response.data.zip;
    callerInfo.value.country = response.data.country;
    callerInfo.value.latitude = response.data.latitude;
    callerInfo.value.longitude = response.data.longitude;
    callerInfo.value.noteToDriver = response.data.noteToDriver;
    callerInfo.value.gourmetNoteToDriver = response.data.gourmetNoteToDriver;
    callerInfo.value.trialInfo = response.data.trialInfo;
    callerInfo.value.isSoftRegSenior = response.data.isSoftRegSenior;
    callerInfo.value.associatedAbTests = response.data.associatedAbTests;
    callerInfo.value.planPricing = response.data.planPricing;
    callerInfo.value.stripeAccountName = response.data.stripeAccountName;

    trialInfo.value = response.data.trialInfo;
    planPricing.value = response.data.planPricing ?? [];
    planCurrency.value = (response.data.currency as 'USD') ?? 'USD';

    if (!trialInfo.value) {
      await handleGourmetUsageQqq(callerInfo.value.id);
      await refreshAbTestInfo(callerInfo.value.phoneNumber);
    }
    setNewCustomerInfo(callerInfo.value);

    if (isPartnerDashboard.value && isApprovedAddressPartner.value && approvedRegistrationAddresses.value.length > 0) {
      const pending = approvedRegistrationAddresses.value.filter((a) => a.latitude && a.longitude && (a.streetAddress || a.city));
      for (const addr of pending) {
        try {
          await callerService.createCallerAddress({
            callerId: String(callerInfo.value.id),
            name: (addr.name || addr.streetAddress) as string,
            streetAddress: addr.streetAddress,
            streetAddress2: addr.streetAddress2,
            city: addr.city,
            state: addr.state,
            zip: addr.zip,
            country: addr.country,
            latitude: addr.latitude ?? undefined,
            longitude: addr.longitude ?? undefined,
            googlePlaceId: addr.googlePlaceId ?? undefined,
            phoneNumber: callerInfo.value.phoneNumber,
            noteToDriver: addr.noteToDriver,
            gourmetNoteToDriver: addr.gourmetNoteToDriver,
            isAirport: addr.isAirport,
          });
        } catch {
          await showWarningPopupDialog({
            text: 'Caller data recorded, but some addresses failed to save — add them from the contact record',
          });
          break;
        }
      }
    }

    emit('goto-next-step');
    emit('valid', true);
  } catch (error) {
    const errorText = util.getErrorResponse(error, false);
    showErrorPopupDialog({
      text: errorText,
      type: 'html',
    });
    emit('valid', false);
  }
};

const refreshAbTestInfo = async (phoneNumber: string) => {
  const response = await callerService.fetchCustomerInfo({
    phoneNumber,
    ...(isPartnerDashboard ? { partnerId: partnerId.value } : {})
  });

  callerInfo.value.associatedAbTests = response.associatedAbTests;
  callerInfo.value.trialInfo = response.trialInfo;
  callerInfo.value.planPricing = response.planPricing;
  trialInfo.value = response.trialInfo ?? null;
};

const handleGourmetUsageQqq = async (id: string) => {
  if (!props.isGourmetRegistration || !gourmetUsage.value) return;

  await userInteractionService.createUserInteraction({
    associatedObjectType: 'caller',
    associatedObjectId: id,
    action: gourmetUsageQqqAbTestName,
    changes: {
      gourmetUsage: gourmetUsage.value,
      gourmetUsageFrequency: gourmetUsageFrequency.value,
    },
  });

  const [usageAssign] = await Promise.all([
    abTestService.assignAbTestPerStrategy({
      abTestName: gourmetUsageQqqAbTestName,
      associatedObjectId: id,
      associatedObjectType: 'caller',
    }),
    abTestService.disableAbTestGroupUser(gourmet7DaysTrialAnnualAbTest, id).catch(() => null),
    abTestService.disableAbTestGroupUser(gourmet7DaysTrialAnyAbTest, id).catch(() => null),
  ]);
  gourmetUsageQqqGroup.value = usageAssign?.groupType || null;

  if (gourmetUsageQqqGroup.value === 'test' && ['1-2', '3-4', '5+'].includes(gourmetUsageFrequency.value)) {
    await abTestService.assignAbTestPerStrategy({
      abTestName: gourmet7DaysTrialAnyAbTest,
      associatedObjectId: id,
      associatedObjectType: 'caller',
    });
  } else if (gourmetUsageQqqGroup.value === 'control') {
    await abTestService.assignAbTestPerStrategy({
      abTestName: gourmet7DaysTrialAnnualAbTest,
      associatedObjectId: id,
      associatedObjectType: 'caller',
    });
  }
};

const updateCustomer = async (data: any) => {
  const { defaultRideAllowanceTier, defaultRideAllowanceAmount, partnerEmergencyContact, partnerProgramManager, partnerCallerNotes, ...postData } = data;
  try {
    await callerService.updateCaller({
      callerId: callerInfo.value.id,
      data: postData,
    });

    if (isPartnerDashboard.value) {
      await savePartnerBudgets();
    }

    if (isPartnerDashboard.value) {
      await partnerCallerService.updatePartnerCaller({
        partnerCode: partnerCodeRef?.value,
        phoneNumber: callerInfo.value.phoneNumber,
        data: {
          defaultRideAllowanceTier: isOnlyCustomRideAllowanceTier.value
            ? 'custom'
            : defaultRideAllowanceTier ?? null,
          defaultRideAllowanceAmount: isCustomRideAllowanceTier.value
            ? util.getOptionalNumber(defaultRideAllowanceAmount)
            : null,
          notes: partnerCallerNotes,
          partnerEmergencyContact,
          ...(isPartnerProgramManagerVisibleAndRequired.value ? { partnerProgramManager } : {}),
        },
      });
    }

    showSuccessPopupDialog({
      text: 'The customer has been updated successfully.',
    });

    callerInfo.value.isSoftRegSenior = isSoftRegSenior.value;

    await handleGourmetUsageQqq(callerInfo.value.id);
    await refreshAbTestInfo(callerInfo.value.phoneNumber);
    setNewCustomerInfo(callerInfo.value);

    emit('goto-next-step');
    emit('valid', true);
  } catch (error) {
    const errorText = util.getErrorResponse(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
    emit('valid', false);
  }
};

const submitForm = async () => {
  if (isPartnerDashboard.value && callerPartnerBudgets.budgetError) {
    showErrorPopupDialog({
      text: callerPartnerBudgets.budgetError,
    });
    return;
  }

  if (!callerInfo.value.email && !callerInfo.value.isAskedUserForEmail) {
    showWarningPopupDialog({
      text: 'Please either ask the customer for email or select the "I have asked the customer for an e-mail and they do not have one" checkbox',
    });

    return;
  }

  const additionalInfoSelected = CallerAdditionalInfoProps.some((prop) => callerInfo.value[prop]);
  if (!additionalInfoSelected) {
    showWarningPopupDialog({
      text: 'Please select either None or something under Additional Information section',
    });

    return;
  }

  callerAddress.value = trimAddress(callerAddress.value);
  callerInfo.value.homeNumber = util.getValidPhoneNumber(callerInfo.value.homeNumber);

  // Post /callers and updateCaller mutation have difference value for preference languages
  const noPreference = isUpdatingStep.value ? languagePreferenceList[0].id : languagePreferenceList[0].key;

  const postData: any = {
    firstName: callerInfo.value.firstName,
    lastName: callerInfo.value.lastName,
    phoneNumber: callerInfo.value.phoneNumber,
    homeNumber: callerInfo.value.homeNumber,
    email: callerInfo.value.email,
    streetAddress: callerAddress.value.streetAddress,
    streetAddress2: callerAddress.value.streetAddress2,
    city: callerAddress.value.city,
    state: callerAddress.value.state,
    zip: callerAddress.value.zip,
    country: callerAddress.value.country,
    homeNote: callerAddress.value.noteToDriver,
    noAdditionalInfo: callerInfo.value.noAdditionalInfo ? true : false,
    isVisuallyImpaired: callerInfo.value.isVisuallyImpaired ? true : false,
    isCane: callerInfo.value.isCane ? true : false,
    isElbowAssist: callerInfo.value.isElbowAssist ? true : false,
    isTraveling: callerInfo.value.isTraveling ? true : false,
    isWalker: callerInfo.value.isWalker ? true : false,
    isWheelchair: callerInfo.value.isWheelchair ? true : false,
    isHardOfHearing: callerInfo.value.isHardOfHearing ? true : false,
    isServiceDog: callerInfo.value.isServiceDog ? true : false,
    isOxygenTank: callerInfo.value.isOxygenTank ? true : false,
    isPoorMemory: callerInfo.value.isPoorMemory ? true : false,
    isNoBigCars: callerInfo.value.isNoBigCars ? true : false,
    isNoSmallCars: callerInfo.value.isNoSmallCars ? true : false,
    isVerballyImpaired: callerInfo.value.isVerballyImpaired ? true : false,
    isSpeech: callerInfo.value.isSpeech ? true : false,
    isSlowWalker: callerInfo.value.isSlowWalker ? true : false,
    isPowerWheelchairScooter: callerInfo.value.isPowerWheelchairScooter ? true : false,
    isAda: callerInfo.value.isAda ? true : false,
    operatorNote: callerInfo.value.operatorNote,
    language1: callerInfo.value.language1,
    language2: callerInfo.value.language2,
    langPrefForUI: callerInfo.value.langPrefForUI || noPreference,
    langPrefForOperator: callerInfo.value.langPrefForOperator || noPreference,
    langPrefForDriver: callerInfo.value.langPrefForDriver || noPreference,
    latitude: callerAddress.value.latitude,
    longitude: callerAddress.value.longitude,
    isAskedUserForEmail: callerInfo.value.isAskedUserForEmail ? true : false,
    isSoftRegSenior: isSoftRegSenior.value,
  };

  if (isPartnerDashboard.value) {
    postData.mailingStreetAddress = mailingAddress.value.streetAddress;
    postData.mailingStreetAddress2 = mailingAddress.value.streetAddress2;
    postData.mailingCity = mailingAddress.value.city;
    postData.mailingState = mailingAddress.value.state;
    postData.mailingZip = mailingAddress.value.zip;
    postData.mailingCountry = mailingAddress.value.country;
    postData.externalPartnerCallerId = callerInfo.value.externalPartnerCallerId;
    postData.partnerEmergencyContact = callerInfo.value.partnerEmergencyContact;
    if (isPartnerProgramManagerVisibleAndRequired.value) {
      postData.partnerProgramManager = callerInfo.value.partnerProgramManager;
    }
    postData.preAuthorizationNumber = callerInfo.value.preAuthorizationNumber;
    postData.partnerSuspensionDate = callerInfo.value.partnerSuspensionDate
      ? util.formatDateTime(callerInfo.value.partnerSuspensionDate, 'YYYY-MM-DD')
      : null;
    postData.partnerBeginningDate = callerInfo.value.partnerBeginningDate
      ? util.formatDateTime(callerInfo.value.partnerBeginningDate, 'YYYY-MM-DD')
      : null;
    postData.partnerId = partnerId.value;
    if (isPartnerFieldVisible('partnerMonthlyBudgetAmount')) {
      postData.partnerMonthlyBudgetAmount = util.getOptionalNumber(callerInfo.value.partnerMonthlyBudgetAmount);
    }
    if (!callerInfo.value.id && isPartnerFieldVisible('partnerTotalBudgetAmount')) {
      postData.partnerTotalBudgetAmount = util.getOptionalNumber(callerInfo.value.partnerTotalBudgetAmount);
    }
    postData.defaultRideAllowanceTier = isOnlyCustomRideAllowanceTier.value
      ? 'custom'
      : callerInfo.value.defaultRideAllowanceTier;
    postData.defaultRideAllowanceAmount = isCustomRideAllowanceTier.value
      ? util.getOptionalNumber(callerInfo.value.defaultRideAllowanceAmount)
      : null;
    postData.partnerCallerNotes = callerInfo.value.partnerCallerNotes;
  }

  if (props.isGroceryRegistration) {
    postData.specialInstructions = callerInfo.value.specialInstructions;
    postData.disabilityType = callerInfo.value.disabilityType;
  }

  if (props.isGourmetRegistration) {
    postData.gourmetHomeNote = callerAddress.value.gourmetNoteToDriver;
    postData.isNoRedMeat = callerInfo.value.isNoRedMeat ? true : false;
    postData.isNutAllergy = callerInfo.value.isNutAllergy ? true : false;
    postData.isDairyAllergy = callerInfo.value.isDairyAllergy ? true : false;
    postData.isGlutenFree = callerInfo.value.isGlutenFree ? true : false;
    postData.isVegan = callerInfo.value.isVegan ? true : false;
    postData.isVegetarian = callerInfo.value.isVegetarian ? true : false;
    postData.isLowSugarDiet = callerInfo.value.isLowSugarDiet ? true : false;
    postData.isLowFatsDiet = callerInfo.value.isLowFatsDiet ? true : false;
    postData.isLowFiberDiet = callerInfo.value.isLowFiberDiet ? true : false;
  }

  if (isHomeAddressChanged.value) {
    const loadingLob = ElLoading.service({
      lock: true,
      text: 'Verifying Address...',
      background: 'rgba(0, 0, 0, 0.7)',
      customClass: 'custom-full-spinner-class',
    });

    isHomeAddressChanged.value = false;
    try {
      await checkForAddressDeliverability(callerAddress.value);

      loadingLob.close();
    } catch (error) {
      const errorText = util.getGraphqlError(error, false);
      showErrorPopupDialog({
        text: `${errorText}<br /><i>*If you believe home address is valid, please click OK and go ahead by clicking Create button again.</i>`,
        title: 'Address Verification Error!',
        type: 'html',
      });

      loadingLob.close();
      return;
    }
  }

  if (
    isMaskedPhoneNumberCall.value &&
    (currentCallFromPhoneNumber.value === callerInfo.value.phoneNumber || currentCallFromPhoneNumber.value === callerInfo.value.homeNumber)
  ) {
    showErrorPopupDialog({
      text: `Please ask the customer for their phone number. ${currentCallFromPhoneNumber.value} is masked phone number.`,
    });
    return;
  }

  const loading = ElLoading.service({
    lock: true,
    text: callerInfo.value.id ? 'Updating Customer...' : 'Creating Customer...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  if (callerInfo.value.id) {
    // Update caller
    if (callerInfo.value.familyId && callerInfo.value.email === callerInfo.value.familyEmail) {
      await showErrorPopupDialog({
        text: `User e-mail cannot be the same as the family's email(${callerInfo.value.email}).`,
      });

      loading.close();
      return;
    }

    if (callerInfo.value.dobYear && callerInfo.value.dobMonth && callerInfo.value.dobDay) {
      const dob = new Date(callerInfo.value.dobYear, callerInfo.value.dobMonth - 1, callerInfo.value.dobDay);

      postData.dob = util.formatDateTime(dob, 'YYYY-MM-DD');
    }

    if (callerInfo.value.registerPlan) {
      postData.registerPlan = callerInfo.value.registerPlan;
    }

    await updateCustomer(postData);
  } else {
    // Create caller
    if (!props.partnerCode) {
      postData.isSoftRegistration = callerInfo.value.isSoftRegistration ? true : false;
    } else {
      postData.partnerCodeName = props.partnerCode;
    }

    postData.dobMonth = callerInfo.value.dobMonth;
    postData.dobDay = callerInfo.value.dobDay;
    postData.dobYear = callerInfo.value.dobYear;

    postData.trialInfo = trialInfo.value;
    if (isPartnerDashboard.value && callerPartnerBudgets.partnerBudgetInput) {
      postData.partnerBudget = callerPartnerBudgets.partnerBudgetInput;
    }

    await createCustomer(postData);
  }
  loading.close();
};

const resetForm = () => {
  callerInfo.value = {
    phoneNumber: '',
    homeNumber: '',
    registerPlan: callerInfo.value.registerPlan,
    partnerEmergencyContact: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
    },
    partnerProgramManager: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
    },
    isAskedUserForEmail: false,
    ...(GVRA_PARTNERS.includes(partnerCodeRef.value) && {
      defaultRideAllowanceTier: 'custom',
    }),
  };

  callerAddress.value = {
    streetAddress: '',
    streetAddress2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    latitude: null,
    longitude: null,
    noteToDriver: '',
    gourmetNoteToDriver: '',
  };

  mailingAddress.value = {
    streetAddress: '',
    streetAddress2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
  };

  softRegSenior.value = '';
  isHomeAddressChanged.value = false;
  lastPhoneNumber.value = '';
  isSameAsHomeAddress.value = false;
  isEmailValid.value = true;
  isExternalPartnerCallerIdValid.value = true;
  isPreAuthorizationNumberValid.value = true;
  approvedRegistrationAddresses.value = [];
  void callerPartnerBudgets.loadBudgets();
};

const resetAddress = () => {
  callerAddress.value = {
    streetAddress: '',
    streetAddress2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    latitude: null,
    longitude: null,
    noteToDriver: '',
    gourmetNoteToDriver: '',
  };
};

const showSpeechRecognitionPopup = async (value: boolean) => {
  if (!value) {
    callerInfo.value.isSpeech = false;
    return;
  }

  const isConfirmed = await showConfirmPopupDialog({
    text: 'Because you selected "visually impaired", would you like to activate voice commands for when you call in? Voice command allows you to press or say commands when you call GoGo.  For example “press or say 1 to get picked up from your home.”',
    title: 'Confirmed with the customer',
  });

  callerInfo.value.isSpeech = !!isConfirmed;
};

const makeMailingAddressFromHomeAddress = () => {
  mailingAddress.value = {
    streetAddress: callerAddress.value.streetAddress || '',
    streetAddress2: callerAddress.value.streetAddress2 || '',
    city: callerAddress.value.city || '',
    state: callerAddress.value.state || '',
    zip: callerAddress.value.zip || '',
    country: callerAddress.value.country || '',
  };
};

const resetHomeAddress = () => {
  if (registrationStartAddress.value?.category === 'home') {
    callerAddress.value = {
      streetAddress: registrationStartAddress.value?.streetAddress || '',
      streetAddress2: registrationStartAddress.value?.streetAddress2 || '',
      city: registrationStartAddress.value?.city || '',
      state: registrationStartAddress.value?.state || '',
      zip: registrationStartAddress.value?.zip || '',
      country: registrationStartAddress.value?.country || '',
      latitude: registrationStartAddress.value?.latitude,
      longitude: registrationStartAddress.value?.longitude,
      noteToDriver: registrationStartAddress.value?.noteToDriver || '',
      gourmetNoteToDriver: registrationStartAddress.value?.gourmetNoteToDriver || '',
    };

    return;
  }

  if (registrationEndAddress.value?.category === 'home') {
    callerAddress.value = {
      streetAddress: registrationEndAddress.value?.streetAddress || '',
      streetAddress2: registrationEndAddress.value?.streetAddress2 || '',
      city: registrationEndAddress.value?.city || '',
      state: registrationEndAddress.value?.state || '',
      zip: registrationEndAddress.value?.zip || '',
      country: registrationEndAddress.value?.country || '',
      latitude: registrationEndAddress.value?.latitude,
      longitude: registrationEndAddress.value?.longitude,
      noteToDriver: registrationEndAddress.value?.noteToDriver || '',
      gourmetNoteToDriver: registrationEndAddress.value?.gourmetNoteToDriver || '',
    };

    return;
  }

  callerAddress.value = { ...emptyAddress };
};

watch(callerAddress, () => {
  isHomeAddressChanged.value = true;

  if (isSameAsHomeAddress.value) {
    makeMailingAddressFromHomeAddress();
  }
});

watch(
  () => callerInfo.value.isPoorMemory,
  (value: boolean) => {
    if (value) {
      showWarningPopupDialog({
        text: 'GoGo’s service is not designed for solitary users that are unable to remember their name, phone number, or destination address.',
        title: 'Remind Customer',
      });
    }
  },
);

watch(() => callerInfo.value.isVisuallyImpaired, showSpeechRecognitionPopup);

watch(
  () => callerInfo.value.noAdditionalInfo,
  (v) => {
    if (!v) {
      return;
    }

    for (const prop of CallerAdditionalInfoProps) {
      if (prop === 'noAdditionalInfo') {
        continue;
      }

      callerInfo.value[prop] = false;
    }
  },
);

watch(registrationStartAddress, resetHomeAddress);
watch(registrationEndAddress, resetHomeAddress);

watch(isSameAsHomeAddress, (value: boolean) => {
  if (!value) {
    return;
  }

  makeMailingAddressFromHomeAddress();
});

useEventListener('reset-registration-data', resetForm);

onMounted(() => {
  callerInfo.value = {
    phoneNumber: '',
    homeNumber: '',
    aLocation: '',
    bLocation: '',
    cLocation: '',
    ...newCustomerInfo.value,
    ...(GVRA_PARTNERS.includes(partnerCodeRef.value) && {
      defaultRideAllowanceTier: newCustomerInfo.value.defaultRideAllowanceTier ?? 'custom',
    }),
    partnerEmergencyContact: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      ...newCustomerInfo.value?.partnerEmergencyContact,
    },
    partnerProgramManager: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      ...newCustomerInfo.value?.partnerProgramManager,
    },
  };

  if (props.isGroceryRegistration) {
    callerAddress.value = groceryAddress.value;
  } else {
    callerAddress.value = {
      streetAddress: callerInfo.value.streetAddress || registrationStartAddress.value?.streetAddress,
      streetAddress2: callerInfo.value.streetAddress2 || registrationStartAddress.value?.streetAddress2,
      city: callerInfo.value.city || registrationStartAddress.value?.city,
      state: callerInfo.value.state || registrationStartAddress.value?.state,
      zip: callerInfo.value.zip || registrationStartAddress.value?.zip,
      country: callerInfo.value.country || registrationStartAddress.value?.country,
      latitude: callerInfo.value.latitude || registrationStartAddress.value?.latitude,
      longitude: callerInfo.value.longitude || registrationStartAddress.value?.longitude,
      noteToDriver: callerInfo.value.noteToDriver || registrationStartAddress.value?.noteToDriver,
      gourmetNoteToDriver: callerInfo.value.gourmetNoteToDriver || registrationStartAddress.value?.gourmetNoteToDriver,
    };
  }

  mailingAddress.value = {
    streetAddress: callerInfo.value.mailingStreetAddress || '',
    streetAddress2: callerInfo.value.mailingStreetAddress2 || '',
    city: callerInfo.value.mailingCity || '',
    state: callerInfo.value.mailingState || '',
    zip: callerInfo.value.mailingZip || '',
    country: callerInfo.value.mailingCountry || '',
  };

  if (callerInfo.value.id) {
    softRegSenior.value = callerInfo.value.isSoftRegSenior ? 'self' : 'lovedOne';
  } else {
    if (currentCallLog.value?.callFlow?.includes('unknownCallerCollectDigitsAsLovedOne_1 ')) {
      softRegSenior.value = 'lovedOne';
    } else if (currentCallLog.value?.callFlow?.includes('unknownCallerCollectDigitsAsLovedOne_2 ')) {
      softRegSenior.value = 'self';
    }
  }
});

onBeforeUnmount(() => {
  setNewCustomerInfo({
    ...callerInfo.value,
    ...callerAddress.value,
    mailingStreetAddress: mailingAddress.value.streetAddress,
    mailingStreetAddress2: mailingAddress.value.streetAddress2,
    mailingCity: mailingAddress.value.city,
    mailingState: mailingAddress.value.state,
    mailingZip: mailingAddress.value.zip,
    mailingCountry: mailingAddress.value.country,
  });
});
</script>

<style lang="scss" scoped>
.operator-script-section {
  margin-top: 10px;
  margin-bottom: 40px;

  .script-title {
    color: #4CAF50;
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 10px;
  }

  .script-content {
    background: #f9f9f9;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    padding: 16px 20px;

    .script-text {
      font-size: 14px;
      color: #555;
      line-height: 1.6;
      font-style: italic;
      margin: 0;

      strong {
        font-style: normal;
        color: #333;
      }
    }
  }

  .questionnaire-section {
    margin-top: 16px;

    .question-label {
      font-size: 14px;
      color: #333;
      margin-bottom: 10px;

      .required {
        color: #dc3545;
      }
    }

    .toggle-button-group {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      .toggle-button {
        padding: 8px 20px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background: #fff;
        color: #333;
        font-size: 14px;
        cursor: pointer;
        transition: all 0.2s ease;
        outline: none;

        &:hover {
          border-color: #4CAF50;
        }

        &.active {
          background: #4CAF50;
          border-color: #4CAF50;
          color: #fff;
        }
      }
    }
  }
}

.available-rides-input {
  max-width: none !important;
  width: 100% !important;
  height: 40px !important;
  text-align: left !important;
}
</style>
